import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import {
  Typography,
  Box,
  Paper,
  Grid,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@material-ui/core'
import { makeStyles, styled } from '@material-ui/core/styles'
import {
  DescriptionOutlined as DescriptionIcon,
  CalendarTodayOutlined as CalendarIcon,
  MonetizationOnOutlined as MonetizationOnIcon,
  CloudUploadOutlined as CloudUploadIcon,
  CheckCircleOutlined as CheckCircleIcon,
  WarningOutlined as WarningIcon,
  ErrorOutlined as ErrorIcon
} from '@material-ui/icons'
import Chart from 'react-apexcharts'
import Button from '../../../../shared/Button'
import Loader from 'components/shared/Loader'
import agingAnalysisData from './aging_analysis_1.json'
import ArBalanceComparison from './ArBalanceComparison'
import { getArBalanceStatus, getArBalance, getPreviousArBalance, uploadArBalanceDocument, requestArBalanceComparison, getArBalanceComparison } from '../../../../../actions/applicants'
import { Toast } from 'components/shared/Toaster'
import CreditCost from 'components/shared/CreditCost'
import AuthService from 'tools/AuthService'
import strings from 'strings'
import { findCreditCost, sortDocsByDate } from 'tools/helper'

const IOSSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#65C466',
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#33cf4d',
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: '#bdbdbd', 
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.5,
      backgroundColor: '#e0e0e0' 
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: '#E9E9EA',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));


const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
    backgroundColor: 'white',
    minHeight: '100vh'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap'
  },
  headerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginBottom: theme.spacing(2)
  },
  headerControls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(1)
  },
  viewModeToggle: {
    display: 'flex',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    padding: '4px',
    border: '1px solid #e1e5e9'
  },
  viewModeButton: {
    minWidth: '80px',
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: 600,
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#e9ecef'
    }
  },
  viewModeButtonActive: {
    backgroundColor: '#fff',
    color: '#1976d2',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)'
  },
  viewModeButtonInactive: {
    backgroundColor: 'transparent',
    color: '#6c757d'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2)
  },
  headerRight: {
    display: 'flex',
    gap: theme.spacing(2)
  },
  title: {
    fontWeight: 600,
    fontSize: '20px'
  },
  card: {
    padding: theme.spacing(3),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    borderRadius: '12px',
    boxShadow: '0px 2px 4px rgba(0,0,0,0.05)',
    border: '1px solid #E0E0E0'
  },
  cardIcon: {
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary
  },
  cardValue: {
    fontWeight: 700,
    marginTop: theme.spacing(1)
  },
  section: {
    marginBottom: theme.spacing(3)
  },
  healthAssessment: {
    padding: theme.spacing(2),
    borderRadius: '12px',
    border: '1px solid #E0E0E0',
    backgroundColor: '#fff'
  },
  healthHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    gap: theme.spacing(1)
  },
  badgeGood: {
    backgroundColor: '#E8F5E9',
    color: '#2E7D32',
    fontWeight: 600,
    borderRadius: '10px',
    height: '24px'
  },
  badgeWarning: {
    backgroundColor: '#FFF8E1',
    color: '#F57C00',
    fontWeight: 600,
    borderRadius: '10px',
    height: '24px',
    border: '1px solid #FBC02D'
  },
  badgeRisk: {
    backgroundColor: '#FFEBEE',
    color: '#C62828',
    fontWeight: 600,
    borderRadius: '10px',
    height: '24px',
    border: '1px solid #EF9A9A'
  },
  chartCard: {
    padding: theme.spacing(3),
    height: '100%',
    borderRadius: '12px',
    border: '1px solid #E0E0E0',
    boxShadow: 'none'
  },
  clientListCard: {
    padding: theme.spacing(1),
    borderRadius: '12px',
    border: '1px solid #E0E0E0',
    boxShadow: 'none'
  },
  clientItem: {
    border: '1px solid #f0f0f0',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderRadius: '8px',
    marginBottom: theme.spacing(1)
  },
  clientIndex: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: '#F5F5F5',
    color: '#757575',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: theme.spacing(2),
    fontSize: '14px',
    fontWeight: 600
  },
  healthIndicatorCard: {
    padding: theme.spacing(2),
    height: '100%',
    borderRadius: '12px',
    border: '1px solid #E0E0E0',
    boxShadow: 'none'
  },
  indicatorHeader: {
    marginBottom: theme.spacing(1)
  },
  '@global': {
    '@keyframes pulse': {
      '0%': { opacity: 1 },
      '50%': { opacity: 0.5 },
      '100%': { opacity: 1 }
    }
  }
}))

const ArBalance = ({ applicationId, creditCosts }) => {
  const classes = useStyles()
  const [compareData, setCompareData] = useState(false)

  const hasArBalancePrivilege = AuthService.hasPrivilege('PERFORM_AR_BALANCE_ANALYSIS')

  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [arBalanceData, setArBalanceData] = useState(null)
  const [previousArBalanceData, setPreviousArBalanceData] = useState(null)
  const [viewMode, setViewMode] = useState('current')
  const [showArBalance, setShowArBalance] = useState(false)
  const [arBalanceStatus, setArBalanceStatus] = useState(null)
  const [isCheckingStatus, setIsCheckingStatus] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [hasPreviousData, setHasPreviousData] = useState(false)

  // Comparison states
  const [ableToCompare, setAbleToCompare] = useState(false)
  const [comparisonStatus, setComparisonStatus] = useState(null)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [comparisonData, setComparisonData] = useState(null)
  const [isLoadingComparison, setIsLoadingComparison] = useState(false)
  

  const handleRequestComparison = useCallback(async () => {
    setShowConfirmDialog(false)
    setIsLoadingComparison(true)
    setCompareData(true)
    
    try {
      await requestArBalanceComparison(applicationId)()
      
      Toast({
        type: 'success',
        message: strings.AR_BALANCE_COMPARISON_STARTED_SUCCESS
      })
      
      setComparisonStatus('IN_PROGRESS')
      
      const pollComparison = setInterval(async () => {
        try {
          const statusResponse = await getArBalanceStatus(applicationId)()
          const newComparisonStatus = statusResponse?.comparisonStatus || 'NOT_STARTED'
          setComparisonStatus(newComparisonStatus)
          
          if (newComparisonStatus === 'COMPLETED') {
            const comparisonResponse = await getArBalanceComparison(applicationId)()
            setComparisonData(comparisonResponse)
            setIsLoadingComparison(false)
            clearInterval(pollComparison)
          } else if (newComparisonStatus === 'NOT_STARTED' || newComparisonStatus?.includes('ERROR')) {
            setIsLoadingComparison(false)
            setCompareData(false)
            clearInterval(pollComparison)
            Toast({
              type: 'error',
              message: strings.AR_BALANCE_COMPARISON_FAILED
            })
          }
        } catch (pollError) {
          console.error('Error polling comparison status:', pollError)
          setIsLoadingComparison(false)
          setCompareData(false)
          clearInterval(pollComparison)
        }
      }, 3000)
      
    } catch (error) {
      console.error('Error requesting comparison:', error)
      Toast({
        type: 'error',
        message: strings.AR_BALANCE_COMPARISON_START_ERROR
      })
      setShowConfirmDialog(false)
      setIsLoadingComparison(false)
      setCompareData(false)
    }
  }, [applicationId])
  
  const checkArBalanceStatus = useCallback(async () => {
    try {
      const statusResponse = await getArBalanceStatus(applicationId)()
      const status = statusResponse?.status || 'NOT_STARTED'
      const ableToCompareFlag = statusResponse?.ableToCompare || false
      const comparisonStatusValue = statusResponse?.comparisonStatus || 'NOT_STARTED'
      
      setArBalanceStatus(status)
      setAbleToCompare(ableToCompareFlag)
      setComparisonStatus(comparisonStatusValue)
      
      if (status === 'COMPLETED') {
        setShowArBalance(true)
        setIsCheckingStatus(false)
      } else if (status === 'IN_PROGRESS') {
        setIsCheckingStatus(true)
        setShowArBalance(false)
      } else {
        setShowArBalance(false)
        setIsCheckingStatus(false)
      }
    } catch (error) {
      console.error('Error checking AR balance status:', error)
      setShowArBalance(false)
      setIsCheckingStatus(false)
      setArBalanceStatus('NOT_STARTED')
      setAbleToCompare(false)
      setComparisonStatus('NOT_STARTED')
    }
  }, [applicationId])
  
  const getDocumentAnalysisCreditCost = () => {
    return findCreditCost(creditCosts, 'DOCUMENT_ANALYSIS')
  }

  const getDocumentComparisonCreditCost = () => {
    return findCreditCost(creditCosts, 'DOCUMENT_COMPARISON')
  }

  const getCurrentDisplayData = () => {
    if (viewMode === 'previous' && previousArBalanceData) {
      return previousArBalanceData
    }
    return arBalanceData
  }

  const apiData = getCurrentDisplayData()
  const fallbackData = agingAnalysisData.insightsResult
  
  const totalAR = apiData?.totals?.totalARBalance || fallbackData.totalAR
  const top5Clients = apiData?.topClientArBalances || fallbackData.top5_clients_metrics.clients
  const healthRating = apiData?.financialHealthAssessment || fallbackData.financialHealthRating
  const takeaways = apiData?.healthIndicators || fallbackData.takeaways
  const top5Percentage = apiData?.customerConcentration?.top5ClientsConcentration || fallbackData.top5_clients_metrics.percentageFromTotalAR
  const top5ClientsArBalance = apiData?.totals?.top5ClientsARBalance 

  useEffect(() => {
    if (!applicationId) {
      setInitialLoading(false)
      return
    }

    let interval = null

    const initializeStatus = async () => {
      try {
        setInitialLoading(false)
        await checkArBalanceStatus()
      } catch (error) {
        console.error('Error initializing AR balance status:', error)
        setInitialLoading(false)
      }
    }

    initializeStatus()

    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [applicationId, checkArBalanceStatus])

  useEffect(() => {
    if (!applicationId || arBalanceStatus !== 'IN_PROGRESS') return

    let interval = null

    // Start polling immediately when status is set to IN_PROGRESS
    interval = setInterval(async () => {
      try {
        const statusResponse = await getArBalanceStatus(applicationId)()
        const status = statusResponse?.status || 'NOT_STARTED'
        setArBalanceStatus(status)
        
        if (status === 'COMPLETED') {
          console.log('Status check: AR balance analysis completed, showing report...')
          setShowArBalance(true)
          setIsCheckingStatus(false)
          clearInterval(interval)
        } else if (status === 'NOT_STARTED' || status === 'THREAD_FAILED' || status?.includes('ERROR')) {
          setShowArBalance(false)
          setIsCheckingStatus(false)
          clearInterval(interval)
        }
      } catch (pollError) {
        console.error('Error polling AR balance status:', pollError)
        setIsCheckingStatus(false)
        setShowArBalance(false)
        clearInterval(interval)
      }
    }, 3000)

    // Cleanup interval when status changes or component unmounts
    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [applicationId, arBalanceStatus])


  useEffect(() => {
    if (applicationId && showArBalance && arBalanceStatus === 'COMPLETED') {
      setLoading(true)
      
      const fetchArBalanceData = async () => {
        try {
          const apiResponse = await getArBalance(applicationId)()

          if (ableToCompare) {
            try {
              const previousResponse = await getPreviousArBalance(applicationId)()
              const [sortedPrevious, sortedCurrent] = sortDocsByDate(previousResponse, apiResponse)
              setPreviousArBalanceData(sortedPrevious)
              setArBalanceData(sortedCurrent)
              setHasPreviousData(true)
            } catch (previousError) {
              setPreviousArBalanceData(null)
              setHasPreviousData(false)
            }
          } else {
            setArBalanceData(apiResponse)
            setPreviousArBalanceData(null)
            setHasPreviousData(false)
          }
        } catch (error) {
          setArBalanceData(null)
        } finally {
          setLoading(false)
        }
      }
      
      fetchArBalanceData()
      console.log('Fetching AR balance data for applicant:', applicationId)
    } else if (showArBalance && arBalanceStatus !== 'COMPLETED') {
      setArBalanceData(null)
      setPreviousArBalanceData(null)
      setHasPreviousData(false)
    }
  }, [applicationId, showArBalance, arBalanceStatus, ableToCompare])


  useEffect(() => {
    if (applicationId && comparisonStatus === 'COMPLETED' && compareData && !comparisonData) {
      const fetchComparisonData = async () => {
        try {
          setIsLoadingComparison(true)
          const comparisonResponse = await getArBalanceComparison(applicationId)()
          setComparisonData(comparisonResponse)
        } catch (error) {
          console.error('Error fetching comparison data:', error)
          Toast({
            type: 'error',
            message: strings.AR_BALANCE_COMPARISON_DATA_LOAD_ERROR
          })
          setCompareData(false)
        } finally {
          setIsLoadingComparison(false)
        }
      }
      
      fetchComparisonData()
    }
  }, [applicationId, comparisonStatus, compareData, comparisonData])



  const handleUploadArBalanceDocument = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    setUploading(true)
    setIsCheckingStatus(true)
    setArBalanceStatus('IN_PROGRESS')
    setShowArBalance(false)
    
    try {
      await uploadArBalanceDocument(applicationId, file)()
      
      Toast({
        type: 'success',
        message: strings.AR_BALANCE_UPLOAD_SUCCESS
      })
      
      // Check status after upload completes
      setTimeout(() => {
        checkArBalanceStatus()
      }, 1000)
      
    } catch (error) {
      Toast({
        type: 'error',
        message: strings.AR_BALANCE_UPLOAD_ERROR
      })
      
      setIsCheckingStatus(false)
      setArBalanceStatus('NOT_STARTED')
      setShowArBalance(false)
    } finally {
      setUploading(false)
    }
  }

  const handleComparisonToggle = (event) => {
    const isChecked = event.target.checked
    
    if (!ableToCompare) {
      // Don't allow toggle if not able to compare
      return
    }
    
    if (isChecked && comparisonStatus === 'NOT_STARTED') {
      setViewMode('current')
      setShowConfirmDialog(true)
    } else if (isChecked && comparisonStatus === 'COMPLETED') {
      setViewMode('current')
      setCompareData(true)
    } else {
      // Turn off comparison
      setCompareData(false)
    }
  }

  // Handle comparison cancel
  const handleCancelComparison = () => {
    setShowConfirmDialog(false)
    setCompareData(false)
  }

  // Format currency
  const formatCurrency = (amount) => {
    const numAmount = Number(amount) || 0
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numAmount)
  }

  const getReportDateDisplay = () => {
    if (compareData && comparisonData?.comparison_report) {
      const { date1, date2 } = comparisonData
      const formatDate = (dateStr) => {
        const date = new Date(dateStr)
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      }
      
      return `${strings.AR_BALANCE_COMPARISON_PERIOD} ${formatDate(date1)} ${strings.AR_BALANCE_COMPARISON_TO} ${formatDate(date2)}`
    } else {
      const currentData = getCurrentDisplayData()
      const reportDate = currentData?.date || agingAnalysisData.generatedDate || new Date().toISOString().split('T')[0]
      const date = new Date(reportDate)
      const formattedDate = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
      const prefix = viewMode === 'previous' ? strings.AR_BALANCE_PREVIOUS_REPORT_GENERATED || 'Previous Report Generated:' : strings.AR_BALANCE_REPORT_GENERATED
      return `${prefix} ${formattedDate}`
    }
  }

  // Chart Data - Using aging brackets from API or fallback to JSON
  const agingData = apiData?.agingBrackets || [
    { bracket: '0-30', total: 11866 },
    { bracket: '31-60', total: 1497 },
    { bracket: '61-90', total: 0 },
    { bracket: '91-120', total: 0 },
    { bracket: '+120', total: 0 }
  ]
  
  const agingSeries = [{
    name: 'Amount',
    data: agingData.map(item => item.total)
  }]

  const agingOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
      fontFamily: 'inherit'
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        columnWidth: '40%',
        distributed: true
      }
    },
    colors: ['#6f5bd8', '#2f7d63', '#ea6f39', '#6fae96', '#a93b22'],
    dataLabels: { enabled: false },
    xaxis: {
      categories: agingData.map(item => item.bracket),
      labels: {
        style: { colors: '#9e9e9e', fontSize: '12px' }
      },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: {
      labels: {
        style: { colors: '#9e9e9e' },
        formatter: (val) => `$${val.toLocaleString()}`
      }
    },
    grid: {
      strokeDashArray: 4,
      borderColor: '#f0f0f0'
    },
    tooltip: {
      enabled: true,
      y: {
        formatter: (val) => `$${val.toLocaleString()}`
      }
    },
    legend: { show: false }
  }

  // Calculate concentration percentages
  
  const concentrationSeries = [
    parseFloat((100 - top5Percentage).toFixed(1)), 
    parseFloat(top5Percentage.toFixed(1))
  ]
  const concentrationOptions = {
    chart: {
      type: 'donut',
      fontFamily: 'inherit'
    },
    labels: [strings.AR_BALANCE_OTHER_CLIENTS, strings.AR_BALANCE_TOP_5_CLIENTS],
    colors: ['#2f7d63', '#ea6f39'],
    plotOptions: {
      pie: {
        donut: {
          size: '70%',
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '14px',
              fontWeight: 600,
              color: '#374151',
              formatter: function (val) {
                return val
              }
            },
            value: {
              show: true,
              fontSize: '20px',
              fontWeight: 600,
              color: '#111827',
              formatter: function (val) {
                return parseFloat(val).toFixed(1) + '%'
              }
            },
            total: {
              show: true,
              showAlways: false,
              label: 'Total',
              fontSize: '14px',
              fontWeight: 600,
              color: '#374151',
              formatter: function (w) {
                return '100%'
              }
            }
          }
        }
      }
    },
    dataLabels: { 
      enabled: true,
      formatter: function (val) {
        return parseFloat(val).toFixed(1) + '%'
      },
      style: {
        fontSize: '12px',
        fontWeight: 600,
        colors: ['#fff']
      }
    },
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
      itemMargin: { horizontal: 10, vertical: 5 },
      markers: { radius: 12 },
      fontSize: '14px',
      fontWeight: 500
    },
    stroke: { show: false },
    tooltip: {
      enabled: true,
      y: {
        formatter: function (val) {
          return parseFloat(val).toFixed(1) + '%'
        }
      }
    }
  }

  // Show initial loader
  if (initialLoading) {
    return (
      <Box className={classes.container}>
        <Loader />
      </Box>
    )
  }


  if (loading || (showArBalance && !getCurrentDisplayData() && arBalanceStatus === 'COMPLETED')) {
    return (
      <Box className={classes.container}>
        <Loader />
      </Box>
    )
  }

  // Show processing screen when status is IN_PROGRESS
  if (isCheckingStatus || arBalanceStatus === 'IN_PROGRESS') {
    return (
      <Box className={classes.container}>
        <Box 
          style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            minHeight: '400px',
            textAlign: 'center'
          }}
        >
          <div style={{ 
            width: '60px', 
            height: '60px', 
            borderRadius: '50%', 
            backgroundColor: '#ff9800',
            marginBottom: '24px',
            animation: 'pulse 1.5s ease-in-out infinite'
          }}></div>
          <Typography variant="h5" style={{ marginBottom: '16px', fontWeight: 600 }}>
            {strings.AR_BALANCE_PROCESSING_TITLE}
          </Typography>
          <Typography variant="body1" style={{ color: '#666', marginBottom: '16px' }}>
            {strings.AR_BALANCE_PROCESSING_MESSAGE}
          </Typography>
        </Box>
      </Box>
    )
  }


  if (!showArBalance) {
    return (
      <Box className={classes.container}>
        <Box 
          style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            minHeight: '400px',
            textAlign: 'center'
          }}
        >
          <Typography variant="h4" style={{ marginBottom: '16px', fontWeight: 600 }}>
            {strings.AR_BALANCE_ANALYSIS_REPORT_TITLE}
          </Typography>
          <Typography 
            variant="body1" 
            style={{ 
              marginBottom: '32px', 
              color: '#666', 
              maxWidth: '500px',
              lineHeight: 1.6 
            }}
          >
            {strings.AR_BALANCE_UPLOAD_DESCRIPTION}
          </Typography>
          
          <input
            accept=".pdf,.jpg,.jpeg,.png,.csv"
            style={{ display: 'none' }}
            id="ar-balance-upload"
            type="file"
            onChange={handleUploadArBalanceDocument}
            disabled={!hasArBalancePrivilege}
          />
          <label htmlFor="ar-balance-upload">
            <Tooltip
              title={!hasArBalancePrivilege ? "You don't have permission to upload AR Balance documents" : ""}
              arrow
              placement="top"
            >
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  component="span"
                  disabled={uploading || !hasArBalancePrivilege}
                  style={{ padding: '12px 32px', fontSize: '1rem' }}
                >
                  {uploading ? strings.AR_BALANCE_UPLOADING : strings.AR_BALANCE_UPLOAD_DOCUMENT}
                  <CreditCost cost={getDocumentAnalysisCreditCost()} />
                </Button>
              </span>
            </Tooltip>
          </label>
        </Box>
      </Box>
    )
  }

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <div className={classes.headerTop}>
          <Typography variant="h6" className={classes.title}>{strings.AR_BALANCE_TITLE}</Typography>

          <div className={classes.headerRight}>
            <input
              accept=".pdf,.jpg,.jpeg,.png,.csv"
              style={{ display: 'none' }}
              id="ar-balance-upload-header"
              type="file"
              onChange={handleUploadArBalanceDocument}
            />
            <label htmlFor="ar-balance-upload-header">
              <Button
                variant="outlined"
                style={{
                  border: '1px solid #ddd',
                  backgroundColor: '#fff',
                  borderRadius: '6px',
                  height: '40px',
                  minHeight: '40px',
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: 500
                }}
                component="span"
                disabled={uploading || isCheckingStatus || isLoadingComparison}
              >
                <CloudUploadIcon style={{ marginRight: '8px', fontSize: '18px' }} />
                {uploading ? strings.AR_BALANCE_UPLOADING : strings.AR_BALANCE_UPLOAD_REPORT} <CreditCost cost={getDocumentAnalysisCreditCost()} />
              </Button>
            </label>
          </div>
        </div>

        <div className={classes.headerControls}>
          {hasPreviousData && !compareData && (
            <Box>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: '8px', fontSize: '12px', fontWeight: 500 }}>
                View Mode
              </Typography>
              <div className={classes.viewModeToggle}>
                <button
                  className={`${classes.viewModeButton} ${viewMode === 'current' ? classes.viewModeButtonActive : classes.viewModeButtonInactive}`}
                  onClick={() => setViewMode('current')}
                  style={{
                    backgroundColor: viewMode === 'current' ? '#fff' : 'transparent',
                    color: viewMode === 'current' ? '#1976d2' : '#6c757d',
                    boxShadow: viewMode === 'current' ? '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)' : 'none',
                    minWidth: '80px',
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: 600,
                    borderRadius: '6px',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {strings.AR_BALANCE_CURRENT || 'Current'}
                </button>
                <button
                  className={`${classes.viewModeButton} ${viewMode === 'previous' ? classes.viewModeButtonActive : classes.viewModeButtonInactive}`}
                  onClick={() => setViewMode('previous')}
                  style={{
                    backgroundColor: viewMode === 'previous' ? '#fff' : 'transparent',
                    color: viewMode === 'previous' ? '#1976d2' : '#6c757d',
                    boxShadow: viewMode === 'previous' ? '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)' : 'none',
                    minWidth: '80px',
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: 600,
                    borderRadius: '6px',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {strings.AR_BALANCE_PREVIOUS || 'Previous'}
                </button>
              </div>
            </Box>
          )}

          <Box>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: '8px', fontSize: '12px', fontWeight: 500 }}>
              Analysis Mode
            </Typography>
            <Tooltip
              title={!ableToCompare ? "Upload a new AR Balance Report to be able to compare with previous report" : ""}
              arrow
              placement="top"
            >
              <span>
                <FormControlLabel
                  control={
                    <IOSSwitch
                      checked={compareData}
                      onChange={handleComparisonToggle}
                      color="primary"
                      size="small"
                      disabled={!ableToCompare || isLoadingComparison}
                    />
                  }
                  style={{ margin: 0 }}
                  label={
                    <Typography
                      variant="body2"
                      style={{
                        marginLeft: 8,
                        color: !ableToCompare ? '#bdbdbd' : 'inherit',
                        fontSize: '13px',
                        fontWeight: 500
                      }}
                    >
                      {strings.AR_BALANCE_COMPARE_LABEL}
                    </Typography>
                  }
                />
              </span>
            </Tooltip>
          </Box>
        </div>
      </div>

      {/* Report Date - Dynamic based on switch state */}
      <Box style={{ marginBottom: '32px', textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary" style={{ fontSize: '14px', fontWeight: 500 }}>
          {getReportDateDisplay()}
        </Typography>
      </Box>

      {/* Show comparison view when switch is toggled */}
      {compareData ? (
        isLoadingComparison ? (
          <Box 
            style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center', 
              minHeight: '400px',
              textAlign: 'center'
            }}
          >
            <div style={{ 
              width: '60px', 
              height: '60px', 
              borderRadius: '50%', 
              backgroundColor: '#2196F3',
              marginBottom: '24px',
              animation: 'pulse 1.5s ease-in-out infinite'
            }}></div>
            <Typography variant="h5" style={{ marginBottom: '16px', fontWeight: 600 }}>
              {strings.AR_BALANCE_GENERATING_COMPARISON_TITLE}
            </Typography>
            <Typography variant="body1" style={{ color: '#666', marginBottom: '16px' }}>
              {strings.AR_BALANCE_ANALYZING_CHANGES}
            </Typography>
          </Box>
        ) : (
          <ArBalanceComparison comparisonData={comparisonData} currentArBalanceData={arBalanceData} />
        )
      ) : (
        <>

          {/* Status Indicator */}
          {isCheckingStatus && (
            <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ 
                  width: '12px', 
                  height: '12px', 
                  borderRadius: '50%', 
                  backgroundColor: '#ff9800',
                  marginRight: '12px',
                  animation: 'pulse 1.5s ease-in-out infinite'
                }}></div>
                <Typography variant="body2" color="textSecondary">
                  {strings.AR_BALANCE_ANALYSIS_PROGRESS}
                </Typography>
              </div>
            </Box>
          )}

          {/* Summary Cards */}
          <Grid container spacing={3} className={classes.section}>
            <Grid item xs={12} md={4}>
              <Paper className={classes.card}>
                <div className={classes.cardIcon}><MonetizationOnIcon fontSize="medium" /></div>
                <Typography variant="caption" color="textSecondary">{strings.AR_BALANCE_TOTAL_AR}</Typography>
                <Typography variant="h4" className={classes.cardValue}>{formatCurrency(totalAR)}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper className={classes.card}>
                <div className={classes.cardIcon}><DescriptionIcon fontSize="medium" /></div>
                <Typography variant="caption" color="textSecondary">{strings.AR_BALANCE_TOP_CLIENTS}</Typography>
                <Typography variant="h4" className={classes.cardValue}>{top5ClientsArBalance}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper className={classes.card}>
                <div className={classes.cardIcon}><CalendarIcon fontSize="medium" /></div>
                <Typography variant="caption" color="textSecondary">{strings.AR_BALANCE_CONCENTRATION}</Typography>
                <Typography variant="h4" className={classes.cardValue}>{top5Percentage.toFixed(1)}%</Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Financial Health Assessment */}
          <Box className={classes.section}>
            <Paper className={classes.healthAssessment}>
              <div className={classes.healthHeader}>
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>{strings.AR_BALANCE_HEALTH_ASSESSMENT}</Typography>
                <Chip
                  icon={<CheckCircleIcon style={{ fontSize: 16, color: '#2E7D32' }} />}
                  label={healthRating.rating}
                  className={classes.badgeGood}
                  size="small"
                  variant="outlined"
                />
              </div>
              <Typography variant="body2" color="textSecondary">
                {healthRating.rationale}
              </Typography>
            </Paper>
          </Box>

          {/* Charts */}
          <Grid container spacing={3} className={classes.section}>
            <Grid item xs={12} md={7}>
              <Paper className={classes.chartCard}>
                <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>{strings.AR_BALANCE_AGING_BUCKETS}</Typography>
                <Box mt={2}>
                  <Chart options={agingOptions} series={agingSeries} type="bar" height={300} />
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper className={classes.chartCard}>
                <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>{strings.AR_BALANCE_CUSTOMER_CONCENTRATION}</Typography>
                <Box mt={2} display="flex" justifyContent="center">
                  <Chart options={concentrationOptions} series={concentrationSeries} type="donut" height={300} />
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Top 5 Clients */}
          <Box className={classes.section}>
            <Paper className={classes.clientListCard} style={{ padding: '16px' }}>
              <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, marginBottom: '16px' }}>
                {strings.AR_BALANCE_TOP_CLIENTS_TITLE}
              </Typography>
              <List disablePadding>
                {top5Clients.map((client, index) => (
                  <ListItem key={index} className={classes.clientItem} divider={index !== top5Clients.length - 1}>
                    <div className={classes.clientIndex}>{index + 1}</div>
                    <ListItemText
                      primary={<Typography variant="body2" style={{ fontWeight: 600 }}>{client.name}</Typography>}
                    />
                    <Typography variant="body2" style={{ fontWeight: 700 }}>
                      {formatCurrency(client.totalArBalance ?? client.totalAR ?? 0)}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Box>

          {/* Health Indicators */}
          <Box>
            <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 16 }}>{strings.AR_BALANCE_HEALTH_INDICATORS}</Typography>
            <Grid container spacing={3}>
              {takeaways.slice(0, 4).map((takeaway, index) => {
                // Determine badge type based on takeaway content
                let badgeType = 'good'
                let BadgeIcon = CheckCircleIcon
                let badgeLabel = 'Good'
                
                const takeawayText = typeof takeaway === 'string' ? takeaway : takeaway.text || ''
                
                if (takeawayText.includes('Weakness') || takeawayText.includes('Risk') || 
                    takeawayText.includes('risk') || takeawayText.includes('High-risk') ||
                    takeawayText.includes('Moderate') || takeawayText.includes('large')) {
                  if (takeawayText.includes('critical') || takeawayText.includes('High-risk')) {
                    badgeType = 'risk'
                    BadgeIcon = ErrorIcon
                    badgeLabel = 'Risk'
                  } else {
                    badgeType = 'warning'
                    BadgeIcon = WarningIcon
                    badgeLabel = 'Warning'
                  }
                }
                
                return (
                  <Grid item xs={12} md={6} key={index}>
                    <Paper className={classes.healthIndicatorCard}>
                      <div className={classes.indicatorHeader}>
                        <Chip
                          icon={<BadgeIcon style={{ fontSize: 16, color: badgeType === 'good' ? '#2E7D32' : badgeType === 'warning' ? '#F57C00' : '#C62828' }} />}
                          label={badgeLabel}
                          className={badgeType === 'good' ? classes.badgeGood : badgeType === 'warning' ? classes.badgeWarning : classes.badgeRisk}
                          size="small"
                          variant="outlined"
                        />
                      </div>
                      <Typography variant="body2">
                        {takeawayText.replace(/^(Strength|Weakness|Risk|Anomaly|Trend|Actionable Insight):\s*/, '')}
                      </Typography>
                    </Paper>
                  </Grid>
                )
              })}
            </Grid>
          </Box>
        </>
      )}
      
      {/* Confirmation Dialog for Comparison */}
      <Dialog
        open={showConfirmDialog}
        onClose={handleCancelComparison}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {strings.AR_BALANCE_GENERATE_COMPARISON_TITLE}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {strings.AR_BALANCE_GENERATE_COMPARISON_MESSAGE}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCancelComparison}
            variant="outlined"
            color="secondary"
          >
            {strings.CANCEL}
          </Button>
          <Tooltip
            title={!hasArBalancePrivilege ? "You don't have permission to generate comparison reports" : ""}
            arrow
            placement="top"
          >
            <span>
              <Button
                onClick={handleRequestComparison}
                variant="contained"
                color="primary"
                disabled={!hasArBalancePrivilege}
              >
                {strings.AR_BALANCE_GENERATE_COMPARISON_TITLE}
                <CreditCost cost={getDocumentComparisonCreditCost()} />
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </div>
  )
}

ArBalance.propTypes = {
  applicationId: PropTypes.string
}

export default ArBalance